import React from 'react';
import { useSearchParams } from 'react-router-dom';

import Link from '@dashboard/components/mui-atoms/Link';
import Header from '@dashboard/components/pages/QueryListPage/PageWrapper/Header';
import { createTableComponent } from '@dashboard/components/pages/QueryListPage/TableList/WpDataGrid';
import {
  type FEmailMessageFragment,
  useEmailMessagesQuery,
  usePlatformQuery,
} from '@dashboard/generated/wp/administrator';
import { useCurrentPage } from '@dashboard/hooks/route/useCurrentPage';
import useQueryDataTableProps from '@dashboard/hooks/useQueryDataTableProps';
import { MenuItem, Paper, Select } from '@mui/material';

import { DateFormatter } from '@waypoint/react/formatters';

import TriggerFailButton from './TriggerFailButton';
import TriggerSendButton from './TriggerSendButton';

const MessagesTable = createTableComponent<FEmailMessageFragment>({
  emptyResults: <>No email messages found.</>,
  columns: [
    {
      headerName: 'Platform',
      renderCell: function PlatformLink({ row: { platformId } }) {
        const { data } = usePlatformQuery({ variables: { id: platformId } });
        return <Link href={`/o/${platformId}`}>{data?.Platform.name ?? platformId}</Link>;
      },
    },
    {
      headerName: 'ID',
      renderCell: ({ row }) => {
        const { platformId, id } = row;
        return <Link href={`/o/${platformId}/events/${id}`}>{id}</Link>;
      },
    },
    { headerName: 'Timestamp', renderCell: ({ row }) => <DateFormatter value={row.createdAt} /> },
    { headerName: 'Status', renderCell: ({ row }) => <>{row.status}</> },
    { headerName: 'Source', renderCell: ({ row }) => <>{row.source}</> },
    { headerName: 'Recipient', renderCell: ({ row }) => <>{row.message.to}</> },
    { headerName: 'From', renderCell: ({ row }) => <>{row.message.from}</> },
    {
      headerName: 'Actions',
      renderCell: ({ row }) => (
        <>
          <TriggerSendButton emailMessage={row} />
          <TriggerFailButton emailMessage={row} />
        </>
      ),
    },
  ],
});

function castEnvironmentFilterValue(v: unknown) {
  switch (v) {
    case 'LIVE':
    case 'SANDBOX':
    case 'ALL':
      return v;
    default:
      return 'LIVE';
  }
}

export default function EmailMessagesListPage() {
  const page = useCurrentPage();

  const [searchParams, setSearchParams] = useSearchParams();

  const environment = castEnvironmentFilterValue(searchParams.get('filter.environment'));
  const setEnvironment = (v: unknown) => {
    setSearchParams((prevParams) => {
      const value = castEnvironmentFilterValue(v);
      if (value === 'LIVE') {
        prevParams.delete('filter.environment');
      } else {
        prevParams.set('filter.environment', value);
      }
      return prevParams;
    });
  };

  const { data } = useEmailMessagesQuery({ variables: { page, filter: { environment } } });
  const { pagination } = useQueryDataTableProps(data?.EmailMessages);
  const nodes = data?.EmailMessages.nodes;
  return (
    <Paper>
      <Header
        title="Activity across workspaces"
        pagination={pagination}
        actions={
          <Select onChange={(ev) => setEnvironment(ev.target.value)} value={environment} size="small">
            <MenuItem value="LIVE">Live</MenuItem>
            <MenuItem value="SANDBOX">Test</MenuItem>
            <MenuItem value="ALL">All</MenuItem>
          </Select>
        }
      />
      <MessagesTable nodes={nodes} />
    </Paper>
  );
}
